import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Input, Button, Radio, message } from 'antd';

import useIdCard from '@/hooks/useIdCard';
import { renderIntlLabel } from '@/utils';
import * as PayApi from '@/api/payment';
import { BizErrorCode } from '@/api/bizCode';
import { fetchDesensitizedInfoByWyuid } from '@/api/guest';
import CardRecharge, { IcardRechargeRef } from '@/components/CardRecharge';
import { parseFen2Currency } from '@/utils/number';

import { RECHARGE_CODE } from './config';
import { VipCardProps } from './interface';
import styles from './payModal.module.scss';

const VipCard: React.FC<VipCardProps> = (props) => {
  // 会员信息
  const defaultVipInfo = {
    accountType: 0, // 会员账户类型
    balance: -1, // 账户余额
    wyuid: '', // 身份标识
    isWyUser: true, // 是否网鱼电酒用户
    isVip: true, // 是否会员
  };

  const { certNo, wyuid, payAmount, payModalVisible, businessType } = props;

  const cardRechargeRef = useRef<IcardRechargeRef>();
  const { idCardInfo, resetCardStates, startRead } = useIdCard();

  const [vipcardForm] = Form.useForm(null);

  const [queryVipInfoLoading, setQueryVipInfoLoading] = useState(false);
  const [vipPayType, setVipPayType] = useState(1); // 1: 读身份证, 2: 扫会员码, 3: 手动输入
  const [idCardNo, setIdCardNo] = useState(''); // 手动输入的证件号
  const [vipInfo, setVipInfo] = useState(defaultVipInfo); // 会员信息
  const [restBalance, setRestBalance] = useState(0); // 扣款后余额
  const [rechargeEnabled, setRechargeEnabled] = useState(false); // 会员充值是否启用

  // 获取会员充值启用状态
  const fetchRechargeStatus = async () => {
    try {
      const res = await PayApi.fetchMemberBusinessInfo();
      if (res && res.status) {
        const { data } = res;
        let enabled;
        if (!data || data.length === 0) {
          enabled = false;
        } else {
          enabled = data.find((item) => item.businessCode === RECHARGE_CODE).status === 0;
        }
        setRechargeEnabled(enabled);
      } else {
        setRechargeEnabled(false);
      }
    } catch (err) {
      setRechargeEnabled(false);
    }
  };

  // 根据接口回调设置会员信息
  const initVipInfo = (data) => {
    const tmpVipInfo = JSON.parse(JSON.stringify(defaultVipInfo));
    if (data === null) {
      // 非电酒用户
      tmpVipInfo.isWyUser = false;
    } else {
      // 判定是否会员
      if (data.memberLevel !== null && data.memberLevel !== '') {
        tmpVipInfo.isWyUser = true;
        tmpVipInfo.isVip = true;
        tmpVipInfo.accountType = data.accountType;
        tmpVipInfo.balance = data.balance;
        tmpVipInfo.wyuid = data.wyuid;

        setRestBalance(data.balance - payAmount);
        if (vipPayType === 1) {
          setIdCardNo(data.certNo);
        }
      } else {
        tmpVipInfo.isVip = false;
      }
    }
    setVipInfo(tmpVipInfo);
    // callback parent
    props.vipInfoChangeCallback(tmpVipInfo);
  };

  // 根据证件号查询会员信息
  const queryVipInfoByWyuidCertNo = async (params) => {
    const { wyuid: uid, certNo: no } = params || {};
    setQueryVipInfoLoading(true);
    try {
      let res: any = {};
      if (uid) {
        res = await fetchDesensitizedInfoByWyuid({ wyuid: uid });
      } else {
        res = await PayApi.fetchVipInfoByCertNo({
          certNo: no,
        });
      }

      if (res && res.status) {
        initVipInfo(res.data);
      } else {
        message.error(res.msg || renderIntlLabel('bill_msg_fail_query_member_info'));
        // set default vipInfo
        setVipInfo(defaultVipInfo);
        // callback parent
        props.vipInfoChangeCallback(defaultVipInfo);
      }
      setQueryVipInfoLoading(false);
    } catch (err) {
      setQueryVipInfoLoading(false);
    }
  };

  let code = '';
  let lastTime = 0;

  // 根据会员码查询会员信息
  const queryVipInfoByBarcode = async (barcode) => {
    // check code
    if (barcode === '' || barcode.length > 18) {
      code = '';
      lastTime = 0;
      return;
    }

    setQueryVipInfoLoading(true);
    try {
      const res = await PayApi.fetchVipInfoByBarcode({
        payCode: barcode,
      });

      if (res && res.status) {
        initVipInfo(res.data);
      } else {
        message.error(res.msg || renderIntlLabel('bill_msg_fail_query_member_info'));
        // set default vipInfo
        setVipInfo(defaultVipInfo);
        // callback parent
        props.vipInfoChangeCallback(defaultVipInfo);
      }
      setQueryVipInfoLoading(false);
    } catch (err) {
      setQueryVipInfoLoading(false);
      // hack error code
      if (
        err?.code === BizErrorCode.VIP_CARD_MG_ERROR ||
        err?.code === BizErrorCode.VIP_CARD_ERROR
      ) {
        message.error(renderIntlLabel('bill_msg_fail_scan_to_retry_for_member'));

        if (vipInfo.wyuid !== '') {
          // set default vipInfo
          setVipInfo(defaultVipInfo);
          // callback parent
          props.vipInfoChangeCallback(defaultVipInfo);
        }
      }
    }
  };

  // 扫码枪事件监听器
  const scannerCallback = useCallback(
    (event) => {
      const nextTime = Date.now();
      const keycode = event.keyCode || event.which || event.charCode;

      if (keycode === 13) {
        if (lastTime && nextTime - lastTime < 30) {
          queryVipInfoByBarcode(code);
        }

        code = '';
        lastTime = 0;
        event.preventDefault();
      } else {
        if (!lastTime) {
          code = String.fromCharCode(keycode);
        } else {
          if (nextTime - lastTime < 30) {
            code += String.fromCharCode(keycode);
          } else {
            code = '';
          }
        }
        lastTime = nextTime;
      }
    },
    [payAmount],
  );

  // 重置状态
  const resetStates = () => {
    setVipPayType(1);
    setIdCardNo('');
    setVipInfo(defaultVipInfo);
    setRestBalance(0);
  };

  // 切换验证方式
  const changeVipPayType = (type) => {
    setVipPayType(type);

    // reset states
    setIdCardNo('');
    setRestBalance(0);
    setVipInfo({ ...defaultVipInfo });

    props.vipInfoChangeCallback({ ...defaultVipInfo });

    // 读身份证
    if (type === 1) {
      startRead();
    } else {
      resetCardStates();
    }

    // 扫会员码
    if (type === 2) {
      // 监听扫码枪
      document.addEventListener('keydown', scannerCallback);
    } else {
      code = '';
      lastTime = 0;
    }
  };

  // 会员卡充值
  const gotoRecharge = () => {
    cardRechargeRef.current!.openDrawer({
      wyuid: vipInfo.wyuid,
      accountType: vipInfo.accountType,
    });
  };

  // 初始化
  useEffect(() => {
    if (payModalVisible && certNo && wyuid && businessType !== 4) {
      setIdCardNo(certNo);
      queryVipInfoByWyuidCertNo({ wyuid, certNo });
    }
    if (payModalVisible && (!certNo || !wyuid) && businessType === 4) {
      // 读取身份证
      startRead();
    }

    fetchRechargeStatus();
  }, [payModalVisible, certNo, wyuid, businessType]);

  // 身份证读取回调
  useEffect(() => {
    if (vipPayType === 1 && idCardInfo.id !== '') {
      queryVipInfoByWyuidCertNo({ wyuid: '', certNo: idCardInfo.id });
    }
  }, [idCardInfo.id]);

  // reset states when unmounted
  useEffect(() => {
    return () => {
      code = '';
      lastTime = 0;
      resetStates();
      resetCardStates();
      // remove scanner listener
      document.removeEventListener('keydown', scannerCallback);
    };
  }, []);

  const renderVipPayTypes = () => {
    if (certNo) return null;

    return (
      <div className={styles['vip-pay-type']}>
        <label>{renderIntlLabel('bill_title_validate_ways')}</label>
        <Radio.Group value={vipPayType} onChange={(e) => changeVipPayType(e.target.value)}>
          <Radio value={1}>{renderIntlLabel('bill_enum_vipPayType_read_id')}</Radio>
          <Radio value={2}>{renderIntlLabel('bill_enum_vipPayType_scan_code')}</Radio>
          <Radio value={3}>{renderIntlLabel('bill_enum_vipPayType_input')}</Radio>
        </Radio.Group>
      </div>
    );
  };

  const renderFormContent = () => {
    if (vipPayType === 1) {
      return (
        <Input
          disabled
          placeholder={renderIntlLabel('bill_msg_please_read_certno')}
          value={idCardNo}
        />
      );
    }

    if (vipPayType === 2) {
      return (
        <Input
          disabled
          placeholder={renderIntlLabel('bill_msg_scan_code_by_wangyu_app')}
          value={idCardNo}
        />
      );
    }

    return (
      <Input.Search
        placeholder={renderIntlLabel('guest_coupon_placeholder_please_input_certno')}
        value={idCardNo}
        onChange={(e) => setIdCardNo(e.target.value)}
        loading={queryVipInfoLoading}
        enterButton={renderIntlLabel('order_action_query')}
        onSearch={() => {
          queryVipInfoByWyuidCertNo({ wyuid: '', certNo: idCardNo });
        }}
      />
    );
  };

  const renderContent = () => {
    if (!vipInfo.isWyUser) {
      return (
        <div className={styles['no-data']}>{renderIntlLabel('bill_msg_query_no_member_info')}</div>
      );
    }

    if (vipInfo.isWyUser && !vipInfo.isVip) {
      return (
        <div className={styles['no-data']}>
          {renderIntlLabel('bill_msg_not_opened_the_membership_and_cannot_store')}
        </div>
      );
    }

    if (vipInfo.isWyUser && vipInfo.isVip) {
      return (
        <>
          <CardRecharge
            ref={cardRechargeRef}
            onCloseDrawer={() => queryVipInfoByWyuidCertNo({ wyuid, certNo: idCardNo })}
          />
          <ul className={styles['balance-info']}>
            <li>
              <label className="text-colon">
                {renderIntlLabel('bill_label_current_remaining_money')}
              </label>
              {vipInfo.balance >= 0 ? (
                <>
                  <span>{parseFen2Currency(vipInfo.balance)}</span>
                  {rechargeEnabled ? (
                    <Button type="link" style={{ marginLeft: 10 }} onClick={() => gotoRecharge()}>
                      {renderIntlLabel('bill_action_charge_money')} &gt;
                    </Button>
                  ) : null}
                </>
              ) : (
                '-'
              )}
            </li>
            <li>
              <label className="text-colon">
                {renderIntlLabel('bill_label_remianing_money_after_deduct')}
              </label>
              {vipInfo.balance >= 0 ? (
                <span className={restBalance < 0 ? styles['rest-balance-negative'] : ''}>
                  {parseFen2Currency(restBalance)}
                </span>
              ) : (
                '-'
              )}
            </li>
          </ul>
        </>
      );
    }

    return null;
  };

  return (
    <div className={styles['pay-content']}>
      {renderVipPayTypes()}
      <Form form={vipcardForm} preserve={false} className={styles['vipCard-form']}>
        <Form.Item label={renderIntlLabel('order_roomorder_cert_no')} colon={false}>
          {renderFormContent()}
        </Form.Item>
      </Form>
      {renderContent()}
    </div>
  );
};

export default VipCard;
